import { Box, Grid, useTheme } from '@mui/material';
import { FailedLoad } from 'common/components/FailedLoad/FailedLoad';
import { Loading } from 'common/components/Loading/Loading';
import { branchActions } from 'common/context/BranchSlice';
import { useAppDispatch } from 'common/context/hooks';
import { commonActions } from 'common/context/CommonSlice';
import { orderActions } from 'context/OrderSlice';
import { useFetch } from 'common/hooks/useFetchSwr';
import { AdminRouteNames } from 'common/model/AdminRouteNames';
import { AdminTablePaths } from 'common/model/AdminTablePaths';
import { Branch } from 'common/model/Branch';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BranchForm } from '../Form/BranchForm';
import { BranchListHeader } from './BranchListHeader';
import { BranchCard } from './BranchCard';
import { AddBranchCard } from './AddBranchCard';
import { BrandManager } from 'common/contact/common/components/Manager/BrandManager';

export const COMPONENT_TABLE_PATH = AdminTablePaths.BRANCH;

export const BranchList = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const [formOpen, setFormOpen] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);

  const handleLogout = () => {
    dispatch(commonActions.setAuth(null));
    dispatch(orderActions.resetOrderState());
    navigate(`/${AdminRouteNames.PREFIX}`);
  };

  const {
    data: branches,
    error,
    isLoading,
  } = useFetch<Branch[]>(AdminTablePaths.BRANCH);

  useEffect(() => {
    if (branches) {
      dispatch(branchActions.setBranches(branches));
    }
  }, [branches, dispatch]);

  const handleBranchSelect = (objectId: string) => {
    navigate(`/${AdminRouteNames.PREFIX}/${objectId}/${AdminRouteNames.HOME}`);
  };

  const handleOpenOrderSite = (objectId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const frontendUrl =
      process.env.REACT_APP_FRONTEND_URL ||
      window.location.origin.replace('owner', '');
    window.open(`${frontendUrl}/${objectId}`, '_blank');
  };

  const handleOpenManagement = (objectId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    handleBranchSelect(objectId);
  };

  const handleEdit = (branch: Branch, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedBranch(branch);
    setFormOpen(true);
  };

  const handleAddNew = () => {
    setSelectedBranch(null);
    setFormOpen(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setSelectedBranch(null);
  };

  if (error) return <FailedLoad />;

  return (
    <>
      <BrandManager />
      <Box
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          minHeight: '100vh',
          backgroundColor: theme.palette.background.paper,
        }}
      >
        {isLoading ? (
          <Loading minHeight="100vh" />
        ) : (
          <>
            <BranchListHeader onLogout={handleLogout} />

            <Grid container spacing={3}>
              {branches?.map((branch) => (
                <Grid
                  key={branch.id}
                  sx={{ maxWidth: '300px!important' }}
                  xs={12}
                  sm={6}
                  md={4}
                  lg={3}
                  item
                >
                  <BranchCard
                    branch={branch}
                    onSelect={handleBranchSelect}
                    onOpenOrderSite={handleOpenOrderSite}
                    onOpenManagement={handleOpenManagement}
                    onEdit={handleEdit}
                  />
                </Grid>
              ))}
              <Grid
                sx={{ maxWidth: '300px!important' }}
                xs={12}
                sm={6}
                md={4}
                lg={3}
                item
              >
                <AddBranchCard onClick={handleAddNew} />
              </Grid>
            </Grid>

            <BranchForm
              open={formOpen}
              handleClose={handleFormClose}
              initialData={selectedBranch}
            />
          </>
        )}
      </Box>
    </>
  );
};
